export type Platform = 'windows' | 'mac' | 'ios' | 'ipad' | 'android';

export type Channel = 'msix' | 'exe' | 'dmg' | 'appstore' | 'play' | 'apk';

export interface ReaderVersion {
  // API trả `id` (laravel-mongodb đổi `_id` thành `id` khi serialize); đọc `_id` là gọi
  // PUT/DELETE /reader-version/undefined — sửa và xoá đều hỏng.
  id: string;
  platform: Platform;
  /** Kênh phát hành; API luôn trả về, bản ghi cũ được quy về kênh mặc định */
  channel: Channel;
  version: string;
  mandatory: boolean;
  active: boolean;
  active_at?: string;
  release_notes?: string;
  download_url?: string;
  // `null` là "xoá đi", và phải gửi được đúng như thế: JSON.stringify bỏ hẳn
  // khoá mang giá trị undefined, dùng undefined để xoá thì server không nhận
  // được gì và giá trị cũ nằm nguyên cạnh một download_url mới.
  file_name?: string | null;
  file_size?: number | null;
  /** sha256 của file cài, app đối chiếu trước khi chạy */
  file_checksum?: string | null;
  created_by?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
  formatted_version?: string;
  platform_display?: string;
  channel_display?: string;
}

export interface ReaderVersionSearchParams {
  platform?: string;
  channel?: string;
  mandatory?: boolean;
  active?: boolean;
  version?: string;
  from_date?: string;
  to_date?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export interface ReaderVersionFiltersState {
  platform: string;
  channel: string;
  mandatory: string;
  active: string;
  version: string;
  from_date: string;
  to_date: string;
}

export interface PlatformStats {
  [platform: string]: {
    total_versions: number;
    latest_version: string | null;
    latest_mandatory: boolean;
    last_updated: string | null;
  };
}

export interface UpdateCheckResult {
  has_update: boolean;
  channel?: Channel;
  /** false nghĩa là download_url là link store: mở trình duyệt, không tải về */
  is_installer?: boolean;
  file_name?: string;
  file_size?: number;
  file_checksum?: string;
  latest_version?: string;
  current_version?: string;
  mandatory?: boolean;
  release_notes?: string;
  download_url?: string;
  version_info?: ReaderVersion;
  message?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  errors?: Record<string, string[]>;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
    from: number;
    to: number;
  };
}

export const PLATFORMS = [
  { value: 'windows', label: 'Windows' },
  { value: 'mac', label: 'macOS' },
  { value: 'ios', label: 'iOS' },
  { value: 'ipad', label: 'iPadOS' },
  { value: 'android', label: 'Android' }
] as const;

/**
 * Kênh phát hành của mỗi nền tảng, theo thứ tự hiển thị. Phải khớp
 * ReaderVersion::CHANNELS bên API.
 */
export const CHANNELS: Record<Platform, Channel[]> = {
  windows: ['msix', 'exe'],
  mac: ['dmg'],
  ios: ['appstore'],
  ipad: ['appstore'],
  android: ['play', 'apk'],
};

/**
 * Kênh của bản ghi không khai channel. Khớp ReaderVersion::DEFAULT_CHANNELS —
 * đây là kênh ĐANG phát hành chứ không phải kênh chính, nên windows là exe và
 * android là play.
 */
export const DEFAULT_CHANNELS: Record<Platform, Channel> = {
  windows: 'exe',
  mac: 'dmg',
  ios: 'appstore',
  ipad: 'appstore',
  android: 'play',
};

export const CHANNEL_LABELS: Record<Channel, string> = {
  msix: 'MSIX',
  exe: 'EXE',
  dmg: 'DMG',
  appstore: 'App Store',
  play: 'Google Play',
  apk: 'APK',
};

/**
 * Đuôi file cài cho phép, theo nền tảng và kênh. Phải khớp
 * ReaderVersion::INSTALLER_EXTENSIONS bên API. Kênh không có mặt ở đây là kênh
 * phát hành qua store, chỉ nhập link.
 */
export const INSTALLER_EXTENSIONS: Partial<Record<Platform, Partial<Record<Channel, string[]>>>> = {
  windows: {
    msix: ['msix', 'msixbundle'],
    exe: ['exe', 'msi'],
  },
  mac: { dmg: ['dmg', 'pkg'] },
  android: { apk: ['apk'] },
};

/** Đuôi file cho phép của một cặp nền tảng + kênh, rỗng nghĩa là chỉ nhập link */
export const installerExtensions = (platform?: Platform, channel?: Channel): string[] => {
  if (!platform || !channel) return [];
  return INSTALLER_EXTENSIONS[platform]?.[channel] ?? [];
};

/**
 * Khớp trần 450MB của API — cố ý THẤP HƠN post_max_size (500M trong
 * docker/frankenphp/php.ini). Sát trần thì phần bao multipart đẩy request vượt
 * ngưỡng PHP, PHP vứt sạch body và server chỉ thấy "thiếu file", trả về một lỗi
 * 422 không liên quan gì tới kích thước.
 */
export const INSTALLER_MAX_BYTES = 450 * 1024 * 1024;

/** Dùng chung cho mọi thông báo về trần, để số không lệch giữa các chỗ. */
export const INSTALLER_MAX_LABEL = '450MB';

/**
 * Khoá trả về KHÔNG trùng tên trường trên bản ghi: `url` và `size` là hợp đồng
 * có sẵn của API, đổi tên sẽ phá bản admin đang chạy. Ánh xạ khi lưu version là
 * url → download_url, size → file_size; hai khoá còn lại giữ nguyên tên.
 */
/** Đuôi của file mô tả mà script phát hành sinh cạnh artifact */
export const DESCRIPTOR_SUFFIX = '.release.json';

export interface InstallerUploadResult {
  url: string;
  file_name: string;
  size: number;
  /** sha256 do server tính, lưu lên bản ghi để app kiểm tra file sau khi tải */
  file_checksum: string;
  extension: string;
  channel: Channel;
  /** Số hiệu đọc từ file mô tả — nguồn duy nhất, không lấy từ ô người gõ */
  version: string;
}

export const PLATFORM_ICONS = {
  windows: '🪟',
  mac: '🍎',
  ios: '📱',
  ipad: '📱',
  android: '🤖'
} as const;
